import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/quienes-somos', label: t('nav.about') },
    { path: '/servicios', label: t('nav.services') },
    { path: '/capacitacion', label: t('nav.training') },
    { path: '/sostenibilidad', label: t('nav.sustainability') },
    { path: '/sectores', label: t('nav.sectors') },
    { path: '/noticias', label: t('nav.news') },
    { path: '/contacto', label: t('nav.contact') },
    ...(user ? [{ path: '/admin', label: 'Admin Panel' }] : [])
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/dcd9015cad73689a5577e684148c10e2.png" 
              alt="Consulting Logistic S.A. Logo" 
              className="h-12 w-auto" 
            />
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#0A5F7F] bg-[#0A5F7F]/10'
                    : 'text-gray-600 hover:text-[#0A5F7F] hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="ml-2 flex items-center gap-1 text-gray-600 hover:text-[#0A5F7F]"
            >
              <Globe className="w-4 h-4" />
              <span className="font-bold">{language === 'es' ? 'EN' : 'ES'}</span>
            </Button>

            {user && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="ml-2 flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Salir</span>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="text-gray-600"
            >
              <span className="font-bold">{language === 'es' ? 'EN' : 'ES'}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#0A5F7F] bg-[#0A5F7F]/10'
                      : 'text-gray-600 hover:text-[#0A5F7F] hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-all duration-200 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Cerrar Sesión
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;